<template>
  <DefaultModal
    :skip-header-underline="true"
    :footer-shadow="true"
    close-btn-color="#fff"
    @close="$emit('close')"
  >
    <template #header>
      <TabHeader :tabs="tabList" @tabClick="handleTabClick" />
    </template>

    <template #body>
      <div ref="bodyWrapper" class="body-wrapper">
        <div v-if="isLoadingData" class="is-fetching-container container">
          <div class="spinner"><BaseSpinner /></div>
        </div>
        <div
          v-else-if="!isLoadingData && showError"
          class="is-fetching-container container"
        >
          <div class="error-container">
            <p>Något gick fel...</p>
            <BaseButton
              :is-rounded="true"
              type="button"
              @click.stop="fetchCurrentTab"
              >Försök igen</BaseButton
            >
          </div>
        </div>
        <template v-else>
          <template v-if="activeTab === 'GENERAL' || activeTab === 'CHAT'">
            <DeleteNoteModal
              v-if="showDeleteNote"
              :note-id="noteToDeleteId"
              :application-id="applicationId"
              :is-manager="isManager"
              :application-landlord-id="applicationLandlordId"
              @close="showDeleteNote = false"
              @onDeleteSuccess="handleDeleteNoteCompleted"
            ></DeleteNoteModal>
            <EditNoteModal
              v-if="activeEditNote"
              :original-note="activeEditNote.text"
              :note-id="activeEditNote.id"
              :application-id="applicationId"
              :is-manager="isManager"
              :application-landlord-id="applicationLandlordId"
              @close="activeEditNote = null"
              @onEditSuccess="handleUpdateNote"
            ></EditNoteModal>
          </template>
          <transition
            v-else-if="activeTab === 'LOG'"
            name="swap-log-fade"
            appear
          >
            <section key="sublet-notes-log" class="swap-log-wrapper container">
              <article v-for="article of log" :key="article.date">
                <h5>
                  {{ new Date(article.date).toLocaleDateString() }}
                  {{
                    new Date(article.date).toLocaleTimeString('sv-SE', {
                      hour: 'numeric',
                      minute: 'numeric'
                    })
                  }}
                </h5>
                <p>{{ getDescription(article) }}</p>
              </article>
            </section>
          </transition>
          <section
            v-if="activeTab === 'GENERAL' || activeTab === 'CHAT'"
            id="scroll-container"
            class="note-wrapper container"
          >
            <transition name="notes-container-fade" appear>
              <NotesContainer key="sublet-notes-list">
                <Note
                  v-for="note in notes"
                  :key="note.id"
                  :note="note"
                  :show-controls="!readOnlyMode && activeTab === 'GENERAL'"
                  :show-is-archived="false"
                  @setActiveEditNote="activeEditNote = $event"
                  @showDeleteNote="
                    showDeleteNote = true;
                    noteToDeleteId = $event;
                  "
                />
              </NotesContainer>
            </transition>
          </section>
        </template>
      </div>
    </template>

    <template #footer>
      <div class="footer-container">
        <div class="textarea-margin-helper">
          <transition
            :name="!readOnlyMode ? 'textarea-toggle' : ''"
            appear
            mode="in-out"
          >
            <section
              v-if="activeTab === 'GENERAL' || activeTab === 'CHAT'"
              class="textarea-wrapper"
            >
              <textarea
                v-if="!readOnlyMode"
                class="area"
                :value="newNote"
                :placeholder="dynamicPlaceholderText"
                cols="30"
                rows="5"
                :disabled="isSaving || isLoadingData || showError"
                @input="newNote = $event.target.value"
              ></textarea>
              <p v-if="activeTab === 'CHAT'" class="info-text">
                OBS! Endast anslutna hyresvärdar får ta del av denna chatt.
              </p>
            </section>
          </transition>
        </div>
        <section class="footer-buttons">
          <BaseButton
            v-if="!readOnlyMode && activeTab !== 'LOG'"
            :is-loading="isSaving"
            :is-rounded="true"
            :is-disabled="disabledSubmitBtn"
            :icon="svgs.ICONS.CHECK_WHITE"
            @click="handleAddNote"
            >Skicka</BaseButton
          >
          <BaseButton
            :is-disabled="isSaving"
            :is-rounded="true"
            :is-default="true"
            @click="$emit('close')"
            >Stäng</BaseButton
          >
        </section>
      </div>
    </template>
  </DefaultModal>
</template>

<script>
import { mapState } from 'vuex';
import { readableAhStatusLog } from '@/utils/statusReason';
import {
  getNotes,
  saveNote,
  getApplicationLog,
  saveManagerNote,
  readNotes
} from '@/services/appService';

import DefaultModal from '@/components/DefaultModal.vue';
import BaseButton from '@/components/BaseButton.vue';
import svgs from '@/utils/icon-constants';
import TabHeader from '@/components/TabHeader.vue';
import BaseSpinner from '@/components/BaseSpinner.vue';
import DeleteNoteModal from '@/components/DeleteNoteModal.vue';
import EditNoteModal from '@/components/EditNoteModal.vue';
import Note from '@/components/Note.vue';
import NotesContainer from '@/components/NotesContainer.vue';

// import churnService from '@/utils/churn360';
// import { events, features } from '@/utils/trackingEvents';

export default {
  name: 'SubletNotesModal',
  components: {
    DefaultModal,
    BaseButton,
    TabHeader,
    BaseSpinner,
    DeleteNoteModal,
    EditNoteModal,
    Note,
    NotesContainer
  },

  props: {
    applicationId: {
      type: String,
      default: null
    },
    unreadNotes: {
      type: Number,
      default: null
    },
    readOnlyMode: {
      type: Boolean,
      default: false
    },
    unreadChatNotes: {
      type: Number,
      default: 0
    },
    applicationLandlordId: {
      type: Number,
      required: true
    },
    applicationType: {
      type: String,
      default: null
    }
  },
  emits: ['activeTabChange', 'close', 'readNotes'],

  data() {
    return {
      svgs,
      isSaving: false,
      notes: null,
      newNote: '',
      log: [],
      activeTab: 'GENERAL',
      isLoadingData: false,
      showError: false,
      showDeleteNote: false,
      noteToDeleteId: null,
      activeEditNote: null
    };
  },

  computed: {
    isManager() {
      return !!this.account?.managerId;
    },
    disabledSubmitBtn() {
      if (this.isLoadingData || this.showError) {
        return true;
      }
      if (!this.newNote || this.newNote.length < 2) {
        return true;
      }
      return false;
    },
    currentNoteType() {
      if (this.activeTab === 'CHAT') {
        return 'CHAT';
      } else if (this.activeTab === 'GENERAL') {
        return 'GENERAL';
      }
      return '';
    },

    ...mapState('app', ['account']),
    accountLandlordId() {
      return this.account.landlordId;
    },
    disableInternalNotes() {
      return !this.applicationId && !this.accountLandlordId;
    },
    tabList() {
      return [
        {
          title: 'Interna noteringar',
          active: this.activeTab === 'GENERAL',
          disabled: this.disableInternalNotes,
          showNotification: this.unreadNotes > 0,
          tabLink: 'GENERAL'
        },
        {
          title: 'Chatt',
          active: this.activeTab === 'CHAT',
          disabled: !this.applicationId,
          hidden: this.applicationType !== 'andrahand-brf',
          showNotification: this.unreadChatNotes > 0,
          tabLink: 'CHAT'
        },
        {
          title: 'Ansökans logg',
          active: this.activeTab === 'LOG',
          disabled: false,
          tabLink: 'LOG'
        }
      ];
    },
    dynamicPlaceholderText() {
      if (this.activeTab === 'CHAT') {
        return 'Skriv ett nytt meddelande här';
      } else if (this.activeTab === 'GENERAL') {
        return 'Skriv en ny notering här';
      }
      return '';
    }
  },

  watch: {
    activeTab: {
      async handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          await this.fetchCurrentTab();
          this.$emit('activeTabChange', newVal);
        }
      },
      immediate: true
    }
  },

  async created() {
    await this.fetchCurrentTab();
  },

  async mounted() {
    if (this.unreadNotes > 0) {
      await readNotes(this.applicationId);
      this.$emit('readNotes');
    }
  },
  methods: {
    getDescription(note) {
      const resObj = readableAhStatusLog(
        {
          firstName: note.firstName,
          landlordName: note.landlordName,
          managerName: note.managerName,
          lastName: note.lastName,
          text: note.text,
          type: note.type || 'INITIAL'
        },
        true
      );

      if (resObj.name) {
        return `${resObj.name} ${resObj.text}`;
      } else {
        return resObj.text;
      }
    },
    handleUpdateNote(note) {
      const noteIndex = this.notes.findIndex(
        n => n.id === this.activeEditNote.id
      );
      if (noteIndex !== -1) {
        this.notes[noteIndex] = {
          ...this.notes[noteIndex],
          text: note,
          editedAt: true
        };
      }
      this.activeEditNote = null;
    },
    handleDeleteNoteCompleted() {
      const noteIndex = this.notes.findIndex(n => n.id === this.noteToDeleteId);
      if (noteIndex !== -1) {
        this.notes[noteIndex] = {
          ...this.notes[noteIndex],
          deletedAt: true
        };
      }
      this.showDeleteNote = false;
    },
    async fetchCurrentTab() {
      this.showError = false;
      this.isLoadingData = true;
      this.clearCurrentData();
      try {
        if (this.activeTab === 'CHAT') {
          this.notes = await getNotes(this.applicationId, ['CHAT']);
        } else if (this.activeTab === 'GENERAL') {
          this.notes = await getNotes(this.applicationId, ['GENERAL']);
        } else if (this.activeTab === 'LOG') {
          this.log = await getApplicationLog(this.applicationId, true);
        }
      } catch {
        this.showError = true;
      }
      this.isLoadingData = false;

      this.scrollToLatestNote();
    },

    async handleAddNote() {
      this.isSaving = true;
      let data = null;
      if (this.isManager) {
        data = await saveManagerNote(
          this.applicationId,
          this.applicationLandlordId,
          this.currentNoteType,
          this.newNote
        );
      } else {
        data = await saveNote(
          this.applicationId,
          this.currentNoteType,
          this.newNote
        );
      }

      this.newNote = '';
      this.notes.push(data);
      this.isSaving = false;

      this.scrollToLatestNote();
    },
    handleTabClick(tabId) {
      this.activeTab = tabId;
    },
    clearCurrentData() {
      this.notes = null;
      this.log = [];
      this.newNote = '';
    },
    scrollToLatestNote() {
      this.$nextTick(() => {
        const scrollWrapper = this.$refs.bodyWrapper?.parentElement;
        if (scrollWrapper) {
          scrollWrapper.scrollTo(0, scrollWrapper.scrollHeight);
        }
      });
    }
  }
};
</script>

<style scoped>
.textarea-margin-helper {
  padding-top: 2.5rem;
}

.note-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.info-text {
  position: absolute;
}

.container {
  width: 44.8rem;
  max-width: 100%;
  margin: 0 auto;
  height: 40vh;
}
.swap-log-wrapper article {
  padding-bottom: 0.8rem;
  font-family: var(--font-inter);
}
.swap-log-wrapper article:nth-last-child(1) {
  padding-bottom: 2rem;
}
.swap-log-wrapper h5,
.swap-log-wrapper p {
  margin: 0;
  font-weight: 400;
  color: var(--color-dark-blue);
}
.swap-log-wrapper h5 {
  font-size: 1.2rem;
}
.swap-log-wrapper p {
  font-size: 1.2rem;
}

.footer-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}
.footer-buttons {
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
  gap: 15px;
}
.body-wrapper {
  position: relative;
}
.is-fetching-container {
  display: flex;
  align-items: center;
  justify-content: center;
}
.spinner {
  height: 5rem;
  width: 5rem;
}
#spinner {
  border-top-color: var(--color-blue);
}
.error-container {
  text-align: center;
}
.error-container p {
  font-size: 1.6rem;
  margin-bottom: 1.5rem;
}
.error-container button {
  font-size: 1.8rem;
}
</style>
